// 2026-02 · Buscador rápido de estado de suplementación por clienta.
// Permite al admin forzar `uses_supplements=true/false` para arreglar
// clientas cuya elección quedó desconectada tras alguna actualización.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FaTimes, FaSearch, FaCheck, FaBan } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupplementsStatusManager({ isOpen, onClose }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/supplements/users?search=${encodeURIComponent(search)}&filter=${filter}&limit=100`
      );
      setUsers(data?.users || []);
    } catch (e) {
      toast.error('Error cargando usuarias: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, filter, load]);

  const toggle = async (userId, newValue) => {
    setSavingId(userId);
    try {
      await axios.patch(`${API}/api/admin/users/${userId}/uses-supplements`, {
        uses_supplements: newValue,
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, uses_supplements: newValue } : u));
      toast.success(`Suplementación ${newValue ? 'activada' : 'desactivada'}`);
    } catch (e) {
      toast.error('No se pudo actualizar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingId(null);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="supplements-status-modal">
      <div className="bg-gray-950 border border-pink-500/40 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-pink-500/30 flex justify-between items-center sticky top-0 bg-gray-950 z-10">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              💊 Estado de suplementación
            </h3>
            <p className="text-xs text-gray-400 mt-1">Forzar `uses_supplements` para clientas desconectadas</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="close-supplements-modal">
            <FaTimes />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px] relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                placeholder="Buscar por nombre o email…"
                className="bg-gray-900 border-gray-700 text-white pl-9"
                data-testid="supplements-search-input"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-white text-sm"
              data-testid="supplements-filter"
            >
              <option value="all">Todas</option>
              <option value="active">💊 Con suplementación</option>
              <option value="inactive">🚫 Sin suplementación</option>
            </select>
            <Button onClick={load} className="bg-pink-600 hover:bg-pink-700" data-testid="supplements-search-btn">
              Buscar
            </Button>
          </div>

          {loading && <div className="text-center py-8 text-gray-400">Cargando…</div>}
          {!loading && users.length === 0 && (
            <div className="text-center py-8 text-gray-400">Sin resultados.</div>
          )}
          {!loading && users.length > 0 && (
            <div className="space-y-2" data-testid="supplements-users-list">
              {users.map(u => (
                <div key={u.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-3 flex-wrap" data-testid={`supplements-user-row-${u.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-semibold text-white">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Plan: {u.subscription_plan || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${u.uses_supplements ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-500/20 text-gray-400'}`}>
                      {u.uses_supplements ? '💊 Activo' : '🚫 Inactivo'}
                    </span>
                    <Button
                      size="sm"
                      variant={u.uses_supplements ? 'outline' : 'default'}
                      className={u.uses_supplements ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                      onClick={() => toggle(u.id, !u.uses_supplements)}
                      disabled={savingId === u.id}
                      data-testid={`supplements-toggle-${u.id}`}
                    >
                      {savingId === u.id ? '…' : (u.uses_supplements ? <><FaBan className="mr-1" /> Desactivar</> : <><FaCheck className="mr-1" /> Activar</>)}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
